import type { ReadonlyURLSearchParams } from 'next/navigation';

import {
  PAYMENT_ERROR_AMBIGUOUS_SEARCH_PARAM_KEYS,
  PAYMENT_ERROR_CODE_SEARCH_PARAM_KEYS,
  PAYMENT_ERROR_MESSAGE_SEARCH_PARAM_KEYS,
  PAYMENT_ERROR_SEARCH_PARAM_KEYS,
} from '../consts/PAYMENT_ERROR_SEARCH_PARAM_KEYS';

export type PaymentFailSearchParamsInfo = {
  errorCode?: string;
  message?: string;
};

const ERROR_CODE_PATTERN = /^R?\d{4}$/i;

export const looksLikePaymentErrorCode = (value: string): boolean => {
  return ERROR_CODE_PATTERN.test(value.trim());
};

const normalizeAmbiguousParamValue = (
  paramValue: string,
): Pick<PaymentFailSearchParamsInfo, 'errorCode' | 'message'> => {
  const trimmedValue = paramValue.trim();

  if (!trimmedValue) {
    return {};
  }

  if (looksLikePaymentErrorCode(trimmedValue)) {
    return { errorCode: trimmedValue };
  }

  return { message: trimmedValue };
};

const readFirstParamValue = (
  searchParams: URLSearchParams | ReadonlyURLSearchParams,
  paramKeys: readonly string[],
): string | undefined => {
  for (const paramKey of paramKeys) {
    const paramValue = searchParams.get(paramKey);

    if (paramValue) {
      return paramValue;
    }
  }

  return undefined;
};

export const extractPaymentFailInfoFromSearchParams = (
  searchParams: URLSearchParams | ReadonlyURLSearchParams,
): PaymentFailSearchParamsInfo => {
  const failInfo: PaymentFailSearchParamsInfo = {};

  const codeParamValue = readFirstParamValue(searchParams, PAYMENT_ERROR_CODE_SEARCH_PARAM_KEYS);
  if (codeParamValue) {
    // May send "R5051" or a comma/JSON-like list — take the first token
    const firstCodeToken = codeParamValue
      .replace(/^[\[\["']+/, '')
      .split(/[,;\s\]]+/)
      .map((token) => token.replace(/['"]/g, '').trim())
      .find(Boolean);

    if (firstCodeToken && looksLikePaymentErrorCode(firstCodeToken)) {
      failInfo.errorCode = firstCodeToken;
    } else if (firstCodeToken) {
      const normalizedAmbiguous = normalizeAmbiguousParamValue(firstCodeToken);
      Object.assign(failInfo, normalizedAmbiguous);
    }
  }

  const messageParamValue = readFirstParamValue(
    searchParams,
    PAYMENT_ERROR_MESSAGE_SEARCH_PARAM_KEYS,
  );
  if (messageParamValue) {
    failInfo.message = messageParamValue;
  }

  for (const paramKey of PAYMENT_ERROR_AMBIGUOUS_SEARCH_PARAM_KEYS) {
    const paramValue = searchParams.get(paramKey);
    if (!paramValue) continue;

    const normalizedAmbiguous = normalizeAmbiguousParamValue(paramValue);
    if (!failInfo.errorCode && normalizedAmbiguous.errorCode) {
      failInfo.errorCode = normalizedAmbiguous.errorCode;
    }
    if (!failInfo.message && normalizedAmbiguous.message) {
      failInfo.message = normalizedAmbiguous.message;
    }
  }

  return failInfo;
};

export const extractPaymentErrorCodeFromSearchParams = (
  searchParams: URLSearchParams | ReadonlyURLSearchParams,
): string | number | undefined => {
  return extractPaymentFailInfoFromSearchParams(searchParams).errorCode;
};

export const stripPaymentReturnSearchParams = (searchParams: URLSearchParams): URLSearchParams => {
  const cleanedParams = new URLSearchParams(searchParams.toString());

  cleanedParams.delete('payment');
  cleanedParams.delete('donation_payment');
  cleanedParams.delete('success');

  for (const paramKey of PAYMENT_ERROR_SEARCH_PARAM_KEYS) {
    cleanedParams.delete(paramKey);
  }

  return cleanedParams;
};
